import React from "react";
import Seo from "@/components/Seo";
import { useSettings } from "@/context/SettingsContext";
import { Linkedin, Mail, Quote } from "lucide-react";

export function AboutPage() {
  const { settings } = useSettings();
  const initials = (settings.founder_name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
  return (
    <section className="mx-auto max-w-[920px] px-4 py-14">
      <Seo title="About us" canonicalPath="/about" />
      <h1 className="font-display text-3xl md:text-4xl font-bold">About {settings.brand_name}.in</h1>
      <p className="text-muted-foreground mt-4 text-[17px]">{settings.about_mission}</p>
      <p className="text-muted-foreground mt-4 text-[17px]">
        {settings.brand_name}.in is owned and operated by{" "}
        <strong className="text-foreground">{settings.founder_name}</strong>, an independent founder on a
        mission to make healthcare discovery faster and friendlier for Indian families.
      </p>
      <h2 id="how" className="font-display text-2xl font-bold mt-10">How it works</h2>
      <ol className="list-decimal list-inside space-y-2 mt-4 text-foreground/85">
        <li>Search by specialty, area or doctor name.</li>
        <li>Pick a slot from the doctor's live schedule and get an OTP.</li>
        <li>Walk into the clinic at your slot time, show the OTP, be seen on time.</li>
      </ol>
      <h2 id="verify" className="font-display text-2xl font-bold mt-10">Doctor verification</h2>
      <p className="mt-3 text-foreground/85">
        Doctors who list their practice are reviewed by our team. We check basic registration details
        and clinic information before marking a profile as Verified. A missing badge simply means we
        haven't completed verification yet.
      </p>

      {/* FOUNDER SECTION */}
      <h2 id="founder" className="font-display text-2xl font-bold mt-14">Meet the founder</h2>
      <article className="mt-5 rounded-2xl border border-border bg-card p-6 md:p-8 flex flex-col md:flex-row items-start gap-6" data-testid="founder-card">
        {settings.founder_photo_url ? (
          <img
            src={settings.founder_photo_url}
            alt={settings.founder_name}
            className="h-28 w-28 md:h-32 md:w-32 rounded-2xl object-cover shrink-0 ring-1 ring-border"
          />
        ) : (
          <div className="h-28 w-28 md:h-32 md:w-32 rounded-2xl bg-gradient-to-br from-primary to-[hsl(var(--ld-jade))] text-white grid place-items-center shrink-0 ring-1 ring-border">
            <span className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
              {initials || "LD"}
            </span>
          </div>
        )}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            {settings.founder_title}
          </p>
          <h3 className="font-display text-2xl md:text-3xl font-bold mt-1" data-testid="founder-name">
            {settings.founder_name}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">Independent founder · Based in India</p>
          <div className="mt-4 relative text-foreground/85 leading-relaxed">
            <Quote size={22} className="absolute -left-1 -top-2 text-primary/20" />
            <p className="pl-6">{settings.founder_bio}</p>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {settings.founder_email && (
              <a
                href={`mailto:${settings.founder_email}`}
                className="btn-secondary py-2 text-[13px]"
                data-testid="founder-email"
              >
                <Mail size={15} /> {settings.founder_email}
              </a>
            )}
            {settings.founder_linkedin_url && (
              <a
                href={settings.founder_linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary py-2 text-[13px]"
                data-testid="founder-linkedin"
              >
                <Linkedin size={15} /> LinkedIn
              </a>
            )}
          </div>
          {!settings.founder_photo_url && (
            <p className="text-xs text-muted-foreground mt-4 italic">
              Photo coming soon — add it from the admin panel.
            </p>
          )}
        </div>
      </article>
    </section>
  );
}

export function PrivacyPage() {
  const { settings } = useSettings();
  return (
    <section className="mx-auto max-w-[920px] px-4 py-14">
      <Seo title="Privacy policy" canonicalPath="/privacy" />
      <h1 className="font-display text-3xl font-bold">Privacy policy</h1>
      <p className="text-muted-foreground mt-4">
        {settings.brand_name}.in is owned and operated by{" "}
        <strong className="text-foreground">{settings.founder_name}</strong>. We collect only the information
        required to help you discover doctors and book appointments — your mobile number, name and the
        appointments you make. We never sell your data. Clinics only receive the details you choose to share
        with them when you contact or book.
      </p>
      <p className="text-muted-foreground mt-4">
        For privacy requests, write to {settings.support_email}.
      </p>
    </section>
  );
}

export function TermsPage() {
  const { settings } = useSettings();
  return (
    <section className="mx-auto max-w-[920px] px-4 py-14">
      <Seo title="Terms & conditions" canonicalPath="/terms" />
      <h1 className="font-display text-3xl font-bold">Terms &amp; conditions</h1>
      <p className="text-muted-foreground mt-4">
        {settings.brand_name}.in is a doctor discovery platform owned and operated by{" "}
        <strong className="text-foreground">{settings.founder_name}</strong>. It does not provide medical
        advice or treatment. Any decisions about your care should be made in consultation with a qualified
        medical professional. By using the site, you agree not to misuse profiles, submit fake information
        or disrupt the service.
      </p>
    </section>
  );
}

export function ContactPage() {
  const { settings } = useSettings();
  return (
    <section className="mx-auto max-w-[720px] px-4 py-14">
      <Seo title="Contact us" canonicalPath="/contact" />
      <h1 className="font-display text-3xl font-bold">Contact {settings.brand_name}.in</h1>
      <p className="text-muted-foreground mt-4">
        Questions, feedback or want to list your clinic? We'd love to hear from you.
      </p>
      <ul className="mt-6 space-y-2 text-foreground/85">
        <li><strong>Founder:</strong> {settings.founder_name}</li>
        <li><strong>Support:</strong> {settings.support_email}</li>
        <li><strong>For clinics:</strong> {settings.doctors_email}</li>
      </ul>
    </section>
  );
}
